/**
 * Bitmiş Ürünler & Teslimat (spec msg-fp-integration, iter57 Faz 2).
 *
 * Tek sayfa iki tab:
 *   [Stok] Aggregate liste — üretim başına 1 satır, checkbox seçim, tekli/toplu teslimat.
 *   [Teslimat Geçmişi] Delivery Group listesi — 1 irsaliye = 1 satır.
 *
 * Backend endpoint'leri:
 *   GET  /api/finished-products/aggregated
 *   GET  /api/finished-products/:id/fillings
 *   POST /api/shipments/bulk-deliver          ← tekli & toplu aynı endpoint
 *   GET  /api/deliveries/grouped-history
 *   GET  /api/deliveries/:sid/detail
 *   POST /api/shipments/:sid/cancel
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Table, Btn, Modal, Input, Textarea, Badge, Card } from "@/components/UI";
import { Truck, Package, History, FileText, X, ChevronDown, Search, Tag, Camera, Pencil, Eye, Send, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { doneLast } from "@/lib/listOrder";
import BoxLabelPreview from "@/components/BoxLabelPreview";
import PhotoModal, { PhotoBadgeButton } from "@/components/PhotoModal";
import { openPdfPreview } from "@/lib/pdfPreview";
import { todayTR } from "@/lib/date";

/* ---------- Küçük yardımcılar ---------- */
const fmtDate = (s) => (s || "").slice(0, 10);
const statusOf = (r) => {
  const t = (r?.total_filled) || 0, d = (r?.delivered) || 0;
  if (t > 0 && d >= t) return { label: "Teslim Edildi", level: "ok" };
  if (d > 0) return { label: "Kısmi Sevk", level: "attention" };
  if (((r?.remaining) || 0) > 0) return { label: "Sevkiyata Hazır", level: "attention" };
  return { label: "Beklemede", level: "inactive" };
};

/* ---------- Stok tab ---------- */
function StockTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [onlyDeliverable, setOnlyDeliverable] = useState(false);
  const [selected, setSelected] = useState({}); // id → { qty }
  const [modal, setModal] = useState(null); // "single" | "bulk" | { kind:"fillings", data }
  const [singleTarget, setSingleTarget] = useState(null);
  const [labelFor, setLabelFor] = useState(null); // {finishedLotId, prodNo}
  const [photoFor, setPhotoFor] = useState(null); // {refId, prodNo}
  const [photoCounts, setPhotoCounts] = useState({});
  const [editPicker, setEditPicker] = useState(null); // {finishedLotId, prodNo, shipments}
  const [editShipmentId, setEditShipmentId] = useState(null);

  const openEditForLot = async (r) => {
    try {
      const ships = await api.get(`/finished-lots/${r.id}/shipments`);
      const active = (ships || []).filter(s => s.status !== "cancelled");
      if (active.length === 0) {
        alert("Bu bitmiş ürüne ait düzenlenebilecek bir teslimat yok.");
        return;
      }
      if (active.length === 1) {
        setEditShipmentId(active[0].id);
      } else {
        setEditPicker({ finishedLotId: r.id, prodNo: r.prod_no, shipments: active });
      }
    } catch (e) {
      alert("Teslimat listesi alınamadı: " + (e.response?.data?.detail || e.message));
    }
  };

  const loadPhotoCounts = async (ids) => {
    if (!ids?.length) return;
    try {
      const r = await api.get(`/photos/counts?context_type=SHIPMENT_EVIDENCE&ref_type=finished_lot&ref_ids=${ids.join(",")}`);
      setPhotoCounts(r || {});
    } catch { /* ignore */ }
  };

  const load = async () => {
    setBusy(true);
    try {
      const url = `/finished-products/aggregated${onlyDeliverable ? "?only_deliverable=true" : ""}`;
      const r = await api.get(url);
      // iter184 Bug 3 defensive — backend always returns a list, but if any
      // network layer returns an object/error blob we must not blow up the page.
      const safe = Array.isArray(r) ? r.filter(x => x && typeof x === "object" && x.id) : [];
      setRows(safe);
      setSelected({}); // reset selection after reload
      loadPhotoCounts(safe.map(x => x.id));
    } catch (e) {
      console.error("finished-products/aggregated failed:", e);
      setRows([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyDeliverable]);

  const filtered = useMemo(() => {
    const sorted = doneLast(rows, r => (r.remaining || 0) <= 0 && (r.delivered || 0) > 0, r => r.last_shipped_at || r.created_at);
    if (!q) return sorted;
    const ql = q.toLowerCase();
    // iter184 Bug 3 defensive — template literal already coerces null/undefined
    // to strings, but guard the .toLowerCase call in case a row is malformed.
    return sorted.filter(r => {
      if (!r || typeof r !== "object") return false;
      const hay = `${r.prod_no ?? ""} ${r.sales_order_no ?? ""} ${r.customer_name ?? ""} ${r.product_name ?? ""} ${r.lot_code ?? ""}`;
      return hay.toLowerCase().includes(ql);
    });
  }, [rows, q]);

  const selCount = Object.keys(selected).length;
  const selCustomer = useMemo(() => {
    const ids = Object.keys(selected);
    if (!ids.length) return null;
    const custs = new Set(ids.map(id => rows.find(r => r.id === id)?.customer_id).filter(Boolean));
    return custs.size === 1 ? [...custs][0] : "MULTI";
  }, [selected, rows]);

  const toggleRow = (r) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[r.id]) delete next[r.id];
      else {
        // aynı müşteri kontrolü (client-side, backend de kontrol ediyor — spec rule 25)
        const first = Object.keys(prev)[0];
        if (first) {
          const firstCust = rows.find(x => x.id === first)?.customer_id;
          if (firstCust !== r.customer_id) {
            alert(`⚠️ Toplu teslimatta yalnız AYNI MÜŞTERİye ait ürünler seçilebilir.\n\nSeçim iptal edilmedi — önce mevcut seçimi temizleyin.`);
            return prev;
          }
        }
        next[r.id] = { qty: r.remaining };
      }
      return next;
    });
  };

  const openFillings = async (r) => {
    try {
      const d = await api.get(`/finished-products/${r.id}/fillings`);
      setModal({ kind: "fillings", data: { ...d, product_name: r.product_name, customer_name: r.customer_name } });
    } catch (e) { alert(e.response?.data?.detail || "Dolum geçmişi alınamadı"); }
  };

  const pg = usePagination(filtered, "finished-products", 100);

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400"/>
          <input data-testid="fp-search" value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Üretim No / Sipariş / Müşteri / Ürün ara..."
                 className="w-full pl-7 pr-3 py-1.5 text-sm border rounded"/>
        </div>
        <label className="text-xs flex items-center gap-1 text-slate-600 cursor-pointer">
          <input type="checkbox" checked={onlyDeliverable} onChange={e=>setOnlyDeliverable(e.target.checked)}/>
          Sadece sevke hazır
        </label>
        <Btn variant="secondary" onClick={load} data-testid="fp-refresh">Yenile</Btn>
        <div className="flex-1"/>
        {selCount > 0 && (
          <Btn onClick={()=>setModal("bulk")} data-testid="fp-bulk-deliver-btn"
               className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Truck size={14}/> Toplu Teslimat ({selCount} kalem)
          </Btn>
        )}
      </div>

      {/* Selected summary bar */}
      {selCount > 0 && selCustomer !== "MULTI" && (
        <div className="mb-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
          ✓ {selCount} kalem seçili — Müşteri: <b>{rows.find(r => r.id === Object.keys(selected)[0])?.customer_name}</b>
          {" · "}<button onClick={()=>setSelected({})} className="underline">Temizle</button>
        </div>
      )}

      {/* Table */}
      <Table testid="fp-agg-table" rows={pg.paged} columns={[
        { label: (
            <input type="checkbox" checked={selCount === filtered.length && filtered.length > 0}
                   onChange={e => {
                     if (e.target.checked && filtered.length > 0) {
                       const firstCust = filtered[0].customer_id;
                       const eligible = filtered.filter(r => r.customer_id === firstCust && r.remaining > 0);
                       const next = {};
                       eligible.forEach(r => next[r.id] = { qty: r.remaining });
                       setSelected(next);
                     } else setSelected({});
                   }}/>
          ), render: r => (
            <input type="checkbox" checked={!!selected[r.id]} disabled={r.remaining <= 0}
                   onChange={()=>toggleRow(r)} data-testid={`fp-select-${r.id}`}/>
          )},
        { label: "Durum", render: r => <Badge level={statusOf(r).level}>{statusOf(r).label}</Badge> },
        { label: "Üretim No", render: r => <span className="font-mono text-xs">{r.prod_no}</span> },
        { label: "Sipariş", render: r => <span className="font-mono text-xs">{r.sales_order_no}</span> },
        { label: "Proje", render: r => <span className="text-xs">{r.project_no}</span> },
        { label: "Müşteri", render: r => r.customer_name },
        { label: "Ürün", render: r => r.product_name },
        { label: "Sipariş Mik.", render: r => <span className="font-mono">{r.order_qty || "—"}</span> },
        { label: "Toplam Dolum", render: r => (
          <button className="font-mono text-blue-600 hover:underline" onClick={()=>openFillings(r)}
                  data-testid={`fp-fillings-${r.id}`}>
            {r.total_filled} <span className="text-[10px] text-slate-500">({r.filling_ops_count}×)</span>
          </button>
        ) },
        { label: "Teslim", render: r => <span className="font-mono">{r.delivered}</span> },
        { label: "Kalan", render: r => (
          <span className={`font-mono font-semibold ${r.remaining > 0 ? "text-emerald-700" : "text-slate-400"}`}>
            {r.remaining} {r.unit}
          </span>
        ) },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            {r.remaining > 0 && (
              <Btn size="sm" onClick={()=>{ setSingleTarget(r); setModal("single"); }}
                   data-testid={`fp-deliver-${r.id}`} title="Teslim Et">
                <Truck size={12}/>
              </Btn>
            )}
            {isAdmin && (r.delivered || 0) > 0 && (
              <Btn size="sm" variant="secondary" onClick={()=>openEditForLot(r)}
                   data-testid={`fp-edit-${r.id}`}
                   className="text-amber-700"
                   title="Teslimatı Düzenle (adet & teslim alan bilgileri)">
                <Pencil size={12}/>
              </Btn>
            )}
            <Btn size="sm" variant="secondary" onClick={()=> setLabelFor({ finishedLotId: r.id, prodNo: r.prod_no })}
                 data-testid={`fp-label-${r.id}`} title="100×100 mm Koli Etiketi">
              <Tag size={12}/>
            </Btn>
            <PhotoBadgeButton
              count={photoCounts[r.id] || 0}
              onClick={() => setPhotoFor({ refId: r.id, prodNo: r.prod_no })}
              testid={`fp-photo-${r.id}`}
              title="Sevkiyat Fotoğrafları"
            />
          </div>
        )},
      ]}/>
      <ListPagination pagination={pg} testid="fp-pagination"/>
      {busy && <div className="text-xs text-slate-500 mt-2">Yükleniyor...</div>}
      {!busy && filtered.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Kayıt yok</div>}

      {/* Fillings modal */}
      <Modal open={modal?.kind === "fillings"} onClose={()=>setModal(null)}
             title={`Dolum Geçmişi — ${modal?.data?.prod_no || ""}`}>
        {modal?.data && (
          <div>
            <div className="text-xs text-slate-500 mb-2">
              Ana stok tek satır olsa da her Dolum ayrı ayrı korunur (spec msg-fp-integration rule 11).
            </div>
            <table className="w-full text-sm border">
              <thead className="bg-slate-100 text-xs">
                <tr>
                  <th className="p-2 text-left">Dolum #</th>
                  <th className="p-2 text-left">Tarih</th>
                  <th className="p-2 text-right">Miktar</th>
                  <th className="p-2 text-left">İşlemi Yapan</th>
                </tr>
              </thead>
              <tbody>
                {(modal.data.filling_operations || []).map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-2 font-mono">D-{String(i+1).padStart(2,"0")}</td>
                    <td className="p-2">{fmtDate(f.timestamp || f.date || f.created_at)}</td>
                    <td className="p-2 text-right font-mono">{f.qty || f.quantity || 0}</td>
                    <td className="p-2">{f.user_name || f.operator || "—"}</td>
                  </tr>
                ))}
                {!(modal.data.filling_operations?.length) && (
                  <tr><td colSpan={4} className="p-4 text-center text-slate-500">Dolum kaydı yok</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Single / Bulk delivery modal */}
      <DeliverModal
        open={modal === "single" || modal === "bulk"}
        mode={modal}
        target={singleTarget}
        rows={rows}
        selected={selected}
        onClose={()=>{ setModal(null); setSingleTarget(null); }}
        onDone={()=>{ setModal(null); setSingleTarget(null); load(); }}
      />

      {/* Merkezi Koli Etiketi — Bitmiş Ürünler kaynağı */}
      <BoxLabelPreview
        open={!!labelFor}
        onClose={() => setLabelFor(null)}
        source="finished"
        finishedLotId={labelFor?.finishedLotId}
        prodNo={labelFor?.prodNo}
      />
      {/* Sevkiyat Fotoğrafları — finished_lot bazlı */}
      <PhotoModal
        open={!!photoFor}
        onClose={() => { setPhotoFor(null); loadPhotoCounts(rows.map(r => r.id)); }}
        contextType="SHIPMENT_EVIDENCE"
        refType="finished_lot"
        refId={photoFor?.refId}
        title={`Sevkiyat Fotoğrafları${photoFor?.prodNo ? ` — ${photoFor.prodNo}` : ""}`}
        showTelegram
        watermark
      />

      {/* Düzenlenecek teslimat seçici (aynı bitmiş lot için >1 sevkiyat varsa) */}
      <Modal open={!!editPicker} onClose={()=>setEditPicker(null)}
             title={`Düzenlenecek Teslimatı Seçin — ${editPicker?.prodNo || ""}`}>
        {editPicker && (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              Bu üretime ait <b>{editPicker.shipments.length}</b> aktif teslimat var. Düzenlenecek olanı seçin:
            </div>
            <table className="w-full text-sm border">
              <thead className="bg-slate-100 text-xs">
                <tr>
                  <th className="p-2 text-left">İrsaliye</th>
                  <th className="p-2 text-left">Tarih</th>
                  <th className="p-2 text-right">Bu Teslim.</th>
                  <th className="p-2 text-left">Teslim Alan</th>
                  <th className="p-2 text-left">Taşıyıcı</th>
                  <th className="p-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {editPicker.shipments.map(s => (
                  <tr key={s.id} className="border-t hover:bg-slate-50">
                    <td className="p-2 font-mono text-xs">{s.dispatch_no}</td>
                    <td className="p-2">{fmtDate(s.delivery_date)}</td>
                    <td className="p-2 text-right font-mono">{s.qty_for_lot}</td>
                    <td className="p-2">{s.recipient || "—"}</td>
                    <td className="p-2">{s.carrier || "—"}</td>
                    <td className="p-2 text-right">
                      <Btn size="sm" onClick={()=>{ setEditPicker(null); setEditShipmentId(s.id); }}
                           data-testid={`edit-pick-${s.id}`}>Düzenle</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Düzenleme modalı — teslim et modalıyla aynı akış */}
      <EditDeliveryModal
        shipmentId={editShipmentId}
        onClose={()=>setEditShipmentId(null)}
        onDone={()=>{ setEditShipmentId(null); load(); }}
      />
    </>
  );
}

/* ---------- Delivery Modal (tekli/toplu aynı akış) ---------- */
function DeliverModal({ open, mode, target, rows, selected, onClose, onDone }) {
  const items = useMemo(() => {
    if (mode === "single" && target) {
      return [{ ...target, qty: target.remaining }];
    }
    if (mode === "bulk") {
      return Object.entries(selected).map(([id, val]) => {
        const r = rows.find(x => x.id === id) || {};
        return { ...r, qty: val.qty };
      });
    }
    return [];
  }, [mode, target, rows, selected]);
  const [itemQtys, setItemQtys] = useState({});
  const [meta, setMeta] = useState({ delivery_date: todayTR(), carrier: "", vehicle: "", tracking_no: "", recipient: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  // [Prompt 02] Toplu Teslimat "Sevkiyat Fotoğrafları" ortak alanı — teslimat oluşmadan
  // önce TEMP context'e, oluştuktan sonra irsaliye/shipment id'sine bağlıyoruz. Bu
  // sayede fotoğraflar Delivery Group'a link'lenir, kalemlere kopyalanmaz.
  const [tempRefId, setTempRefId] = useState(null);
  // [msg-fp-post-deliver] After successful delivery we keep the modal open so the user
  // can still attach photos, open/print the İrsaliye PDF, and close explicitly.
  const [deliveryResult, setDeliveryResult] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const shipmentId = deliveryResult?.id;
  const photoRefId = shipmentId || tempRefId;
  const photoRefType = shipmentId ? "shipment" : "temp_delivery";
  const photoContext = shipmentId ? "SHIPMENT_EVIDENCE" : "SHIPMENT_EVIDENCE_TEMP";

  const refreshPhotoCount = useCallback(async () => {
    if (!photoRefId) return;
    try {
      const m = await api.get(`/photos/counts?context_type=${photoContext}&ref_type=${photoRefType}&ref_ids=${photoRefId}`);
      setPhotoCount(m[photoRefId] || 0);
    } catch { setPhotoCount(0); }
  }, [photoRefId, photoContext, photoRefType]);

  useEffect(() => { if (open) refreshPhotoCount(); }, [open, refreshPhotoCount]);

  useEffect(() => {
    if (open) {
      const q = {};
      items.forEach(it => { q[it.id] = it.qty; });
      setItemQtys(q);
      setMeta({ delivery_date: todayTR(), carrier: "", vehicle: "", tracking_no: "", recipient: "", notes: "" });
      setDeliveryResult(null);
      const rnd = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      setTempRefId(rnd);
      setPhotoCount(0);
    }
  }, [open, items]);

  if (!open) return null;
  const customerName = items[0]?.customer_name || "—";
  const totalQty = Object.values(itemQtys).reduce((s, v) => s + (parseInt(v)||0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        items: items.map(it => ({
          finished_lot_id: it.id,
          qty: parseInt(itemQtys[it.id] || 0),
        })).filter(x => x.qty > 0),
        ...meta,
        idempotency_key: `ui-${Date.now()}-${items.map(i=>i.id).join(":").slice(0,40)}`,
      };
      if (!payload.items.length) { alert("En az bir kalem için miktar girin."); setBusy(false); return; }
      const r = await api.post("/shipments/bulk-deliver", payload);
      // [Prompt 02] Teslimat başarılı: TEMP fotoğrafları oluşan İrsaliye/shipment id'sine link'le
      if (r?.id && tempRefId) {
        try {
          const tempPhotos = await api.get(`/photos?context_type=SHIPMENT_EVIDENCE_TEMP&ref_type=temp_delivery&ref_id=${tempRefId}`);
          if (tempPhotos?.length) {
            await api.post("/photos/link", {
              photo_ids: tempPhotos.map(p => p.id),
              new_context_type: "SHIPMENT_EVIDENCE",
              new_ref_type: "shipment",
              new_ref_id: r.id,
            });
          }
        } catch (linkErr) { /* non-fatal — foto'lar hâlâ TEMP'te durur, kullanıcı manuel ekleyebilir */ }
      }
      setDeliveryResult(r);
    } catch (e) {
      alert("❌ Teslimat başarısız:\n" + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const openIrsaliyePdf = async () => {
    if (!deliveryResult?.id) return;
    setPdfBusy(true);
    try {
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipments/${deliveryResult.id}/irsaliye.pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { alert("PDF alınamadı: HTTP " + resp.status); return; }
      const blob = await resp.blob();
      openPdfPreview(blob, `Irsaliye-${deliveryResult.dispatch_no || deliveryResult.shipment_no || deliveryResult.id}.pdf`);
    } catch (e) { alert("PDF açılamadı: " + e.message); }
    finally { setPdfBusy(false); }
  };

  const handleClose = () => {
    // When a delivery was already created, notify the parent to refresh listings.
    if (deliveryResult) onDone(); else onClose();
  };

  const isDelivered = !!deliveryResult;
  const dispatchNo = deliveryResult?.dispatch_no || deliveryResult?.shipment_no;
  const deliveredTotal = deliveryResult?.lines?.reduce((s, l) => s + (l.qty || 0), 0) || 0;

  return (
    <Modal open={open} onClose={handleClose} title={mode === "bulk" ? `Toplu Teslimat — ${items.length} Kalem` : `Teslim Et — ${items[0]?.product_name || ""}`}>
      {isDelivered ? (
        <div className="text-sm mb-3 space-y-1 bg-emerald-50 border border-emerald-200 p-3 rounded" data-testid="deliver-success-panel">
          <div className="text-emerald-800 font-semibold flex items-center gap-2">✅ Teslimat oluşturuldu</div>
          <div><b>İrsaliye No:</b> <span className="font-mono">{dispatchNo}</span></div>
          <div><b>Müşteri:</b> {customerName}</div>
          <div><b>Kalem:</b> {deliveryResult?.lines?.length || 0} · <b>Toplam:</b> <span className="font-mono">{deliveredTotal}</span></div>
          <div className="text-[11px] text-emerald-700 mt-1">Bu pencere açık kalır: fotoğraf ekleyebilir, PDF yazdırabilir veya kapatabilirsiniz.</div>
        </div>
      ) : (
        <div className="text-sm mb-3 space-y-1 bg-slate-50 p-3 rounded">
          <div><b>Müşteri:</b> {customerName}</div>
          <div><b>Kalem:</b> {items.length} <span className="text-slate-400">·</span> <b>Toplam:</b> <span className="font-mono">{totalQty}</span></div>
          <div className="text-xs text-slate-500">İrsaliye numarası onay sonrası atomik counter'dan atanır (IRS-xxxxx).</div>
        </div>
      )}

      {/* Items table */}
      <div className="max-h-64 overflow-y-auto mb-3 border rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-1.5 text-left">Sipariş</th>
              <th className="p-1.5 text-left">Ürün</th>
              <th className="p-1.5 text-right">Kalan</th>
              <th className="p-1.5 text-right">Teslim Miktarı</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t">
                <td className="p-1.5 font-mono">{it.sales_order_no}</td>
                <td className="p-1.5">{it.product_name}</td>
                <td className="p-1.5 text-right font-mono">{it.remaining} {it.unit}</td>
                <td className="p-1.5 text-right">
                  <input type="number" min="0" max={it.remaining}
                         value={itemQtys[it.id] || ""}
                         onChange={e => setItemQtys(prev => ({ ...prev, [it.id]: e.target.value }))}
                         data-testid={`bulk-qty-${it.id}`}
                         disabled={isDelivered}
                         className="w-20 px-1 py-0.5 text-right border rounded font-mono disabled:bg-slate-100 disabled:text-slate-500"/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Meta fields */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Input label="Teslim Tarihi" type="date" value={meta.delivery_date} disabled={isDelivered}
               onChange={e=>setMeta({...meta, delivery_date: e.target.value})} data-testid="bulk-delivery-date"/>
        <Input label="Taşıyıcı / Şoför" value={meta.carrier} disabled={isDelivered}
               onChange={e=>setMeta({...meta, carrier: e.target.value})} data-testid="bulk-carrier"/>
        <Input label="Araç Plaka" value={meta.vehicle} disabled={isDelivered}
               onChange={e=>setMeta({...meta, vehicle: e.target.value})} data-testid="bulk-vehicle"/>
        <Input label="Takip No" value={meta.tracking_no} disabled={isDelivered}
               onChange={e=>setMeta({...meta, tracking_no: e.target.value})} data-testid="bulk-tracking"/>
      </div>
      <Textarea label="Açıklama" rows={2} value={meta.notes} disabled={isDelivered}
                onChange={e=>setMeta({...meta, notes: e.target.value})} data-testid="bulk-notes"/>

      <div className="flex flex-wrap gap-2 mt-4">
        {!isDelivered && (
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Vazgeç</Btn>
        )}
        {photoRefId && (
          <Btn variant="secondary" onClick={() => setPhotoOpen(true)} data-testid="deliver-modal-photo-btn">
            <Camera size={12}/> Sevkiyat Fotoğrafları {photoCount > 0 ? `(${photoCount})` : ""}
          </Btn>
        )}
        {isDelivered && (
          <Btn variant="secondary" onClick={openIrsaliyePdf} disabled={pdfBusy} data-testid="deliver-modal-pdf-btn">
            📄 {pdfBusy ? "Açılıyor..." : "İrsaliye Yazdır"}
          </Btn>
        )}
        <div className="flex-1"/>
        {isDelivered ? (
          <Btn onClick={handleClose}
               className="bg-slate-700 hover:bg-slate-800 text-white"
               data-testid="deliver-modal-close-btn">
            Kapat
          </Btn>
        ) : (
          <Btn onClick={submit} disabled={busy || totalQty === 0}
               className="bg-emerald-600 hover:bg-emerald-700 text-white"
               data-testid="bulk-confirm-btn">
            {busy ? "Oluşturuluyor..." : "TESLİMATI ONAYLA VE İRSALİYE OLUŞTUR"}
          </Btn>
        )}
      </div>
      <PhotoModal
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        contextType={photoContext}
        refType={photoRefType}
        refId={photoRefId}
        title={`Sevkiyat Fotoğrafları${dispatchNo ? ` — ${dispatchNo}` : ""}${!shipmentId ? " (Ön Yükleme)" : ""}`}
        showTelegram={!!shipmentId}
        watermark={!!shipmentId}
        onChange={refreshPhotoCount}
      />
    </Modal>
  );
}

/* ---------- Edit Delivery Modal (admin only, iter294b) ----------
 * Teslimat düzenleme akışı. "Teslim Et" modalıyla aynı görsel yapı ancak
 * PUT /shipments/{sid} çağırır. Kalem miktarları + teslim alan kişi ve
 * meta alanlar (taşıyıcı, araç plaka, takip no, açıklama, tarih) güncellenir.
 * Backend delta hesaplayarak finished_lot stok, sales_order qty_shipped ve
 * irsaliye tarafındaki `lines` verilerini eşzamanlı senkronlar. */
function EditDeliveryModal({ shipmentId, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rawShipment, setRawShipment] = useState(null); // customer_id, meta
  const [itemQtys, setItemQtys] = useState({});
  const [meta, setMeta] = useState({ delivery_date: "", carrier: "", vehicle: "", tracking_no: "", recipient: "", notes: "" });
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const open = !!shipmentId;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const d = await api.get(`/deliveries/${shipmentId}/detail`);
        if (cancelled) return;
        setDetail(d);
        // Meta değerleri (recipient dahil) detail'den — düzenle-öncesi durumu koru
        setMeta({
          delivery_date: (d.delivery_date || "").slice(0, 10),
          carrier: d.carrier || "",
          vehicle: d.vehicle || "",
          tracking_no: d.tracking_no || "",
          recipient: d.recipient || "",
          notes: d.notes || "",
        });
        const q = {};
        (d.items || []).forEach(it => { q[it.finished_lot_id] = it.this_delivery; });
        setItemQtys(q);
        setRawShipment({ id: d.id, dispatch_no: d.dispatch_no, customer_id: d.customer_id, customer_name: d.customer_name });
        // Foto sayısı
        try {
          const m = await api.get(`/photos/counts?context_type=SHIPMENT_EVIDENCE&ref_type=shipment&ref_ids=${shipmentId}`);
          setPhotoCount(m[shipmentId] || 0);
        } catch { setPhotoCount(0); }
      } catch (e) {
        alert("Teslimat detayı alınamadı: " + (e.response?.data?.detail || e.message));
        onClose?.();
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [shipmentId, open]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const items = detail?.items || [];
  const totalQty = Object.values(itemQtys).reduce((s, v) => s + (parseInt(v) || 0), 0);

  const submit = async () => {
    if (!detail) return;
    const payload = {
      items: items.map(it => ({
        finished_lot_id: it.finished_lot_id,
        qty: parseInt(itemQtys[it.finished_lot_id] || 0),
      })),
      ...meta,
    };
    if (payload.items.some(x => x.qty <= 0)) {
      alert("Her kalem için 0'dan büyük bir miktar girin. Kalem silmek için önce sevkiyatı iptal edin.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/shipments/${shipmentId}`, payload);
      alert(`✅ Teslimat güncellendi (${detail.dispatch_no}). Stok, satış siparişi ve irsaliye senkronize edildi.`);
      onDone?.();
    } catch (e) {
      alert("❌ Güncelleme başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const openIrsaliyePdf = async () => {
    if (!shipmentId) return;
    try {
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipments/${shipmentId}/irsaliye.pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { alert("PDF alınamadı: HTTP " + resp.status); return; }
      const blob = await resp.blob();
      openPdfPreview(blob, `Irsaliye-${detail?.dispatch_no || shipmentId}.pdf`);
    } catch (e) { alert("PDF açılamadı: " + e.message); }
  };

  return (
    <Modal open={open} onClose={onClose}
           title={`Teslimatı Düzenle — ${detail?.dispatch_no || "…"}`}>
      {busy ? (
        <div className="text-sm text-slate-500 py-4">Yükleniyor…</div>
      ) : detail ? (
        <>
          <div className="text-sm mb-3 space-y-1 bg-amber-50 border border-amber-200 p-3 rounded">
            <div className="text-amber-800 font-semibold flex items-center gap-2">
              <Pencil size={14}/> Düzenleme Modu (Admin)
            </div>
            <div><b>İrsaliye No:</b> <span className="font-mono">{detail.dispatch_no}</span></div>
            <div><b>Müşteri:</b> {detail.customer_name || "—"}</div>
            <div className="text-[11px] text-amber-700">
              Miktar değişiklikleri stoktan otomatik düşer/iade eder. Satış Siparişi ve İrsaliye satırları senkronize güncellenir. Kalem eklemek/silmek için sevkiyatı iptal edip yeniden oluşturun.
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto mb-3 border rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-1.5 text-left">Sipariş</th>
                  <th className="p-1.5 text-left">Ürün</th>
                  <th className="p-1.5 text-right">Mevcut Stok</th>
                  <th className="p-1.5 text-right">Bu Teslim. (Yeni)</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const oldQty = it.this_delivery;
                  const availableForIncrease = (it.remaining_after || 0) + oldQty;
                  return (
                    <tr key={it.finished_lot_id} className="border-t">
                      <td className="p-1.5 font-mono">{it.sales_order_no}</td>
                      <td className="p-1.5">{it.product_name}</td>
                      <td className="p-1.5 text-right font-mono text-slate-500">
                        {it.remaining_after} {it.unit} <span className="text-[10px]">(mak. {availableForIncrease})</span>
                      </td>
                      <td className="p-1.5 text-right">
                        <input type="number" min="1" max={availableForIncrease}
                               value={itemQtys[it.finished_lot_id] || ""}
                               onChange={e => setItemQtys(prev => ({ ...prev, [it.finished_lot_id]: e.target.value }))}
                               data-testid={`edit-qty-${it.finished_lot_id}`}
                               className="w-24 px-1 py-0.5 text-right border rounded font-mono"/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <Input label="Teslim Tarihi" type="date" value={meta.delivery_date}
                   onChange={e=>setMeta({...meta, delivery_date: e.target.value})}
                   data-testid="edit-delivery-date"/>
            <Input label="Teslim Alan" value={meta.recipient}
                   onChange={e=>setMeta({...meta, recipient: e.target.value})}
                   data-testid="edit-recipient"/>
            <Input label="Taşıyıcı / Şoför" value={meta.carrier}
                   onChange={e=>setMeta({...meta, carrier: e.target.value})}
                   data-testid="edit-carrier"/>
            <Input label="Araç Plaka" value={meta.vehicle}
                   onChange={e=>setMeta({...meta, vehicle: e.target.value})}
                   data-testid="edit-vehicle"/>
            <Input label="Takip No" value={meta.tracking_no}
                   onChange={e=>setMeta({...meta, tracking_no: e.target.value})}
                   data-testid="edit-tracking"/>
            <div/>
          </div>
          <Textarea label="Açıklama" rows={2} value={meta.notes}
                    onChange={e=>setMeta({...meta, notes: e.target.value})}
                    data-testid="edit-notes"/>

          <div className="flex flex-wrap gap-2 mt-4">
            <Btn variant="secondary" onClick={onClose} disabled={saving}>Vazgeç</Btn>
            <Btn variant="secondary" onClick={()=>setPhotoOpen(true)} data-testid="edit-photo-btn">
              <Camera size={12}/> Sevkiyat Fotoğrafları {photoCount > 0 ? `(${photoCount})` : ""}
            </Btn>
            <Btn variant="secondary" onClick={openIrsaliyePdf} data-testid="edit-pdf-btn">
              <FileText size={12}/> İrsaliye Yazdır
            </Btn>
            <div className="flex-1"/>
            <div className="text-xs text-slate-500 self-center">Toplam: <span className="font-mono">{totalQty}</span></div>
            <Btn onClick={submit} disabled={saving || totalQty === 0}
                 className="bg-amber-600 hover:bg-amber-700 text-white"
                 data-testid="edit-confirm-btn">
              {saving ? "Kaydediliyor..." : "DEĞİŞİKLİKLERİ KAYDET"}
            </Btn>
          </div>
          <PhotoModal
            open={photoOpen}
            onClose={() => setPhotoOpen(false)}
            contextType="SHIPMENT_EVIDENCE"
            refType="shipment"
            refId={shipmentId}
            title={`Sevkiyat Fotoğrafları — ${detail.dispatch_no}`}
            showTelegram
            watermark
            onChange={async () => {
              try {
                const m = await api.get(`/photos/counts?context_type=SHIPMENT_EVIDENCE&ref_type=shipment&ref_ids=${shipmentId}`);
                setPhotoCount(m[shipmentId] || 0);
              } catch {}
            }}
          />
        </>
      ) : null}
    </Modal>
  );
}

/* ---------- Teslimat Geçmişi tab ---------- */
function HistoryTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canWrite = ["admin", "manager", "logistics", "sales"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [invoiceF, setInvoiceF] = useState(""); // "" | "not_invoiced" | "invoiced"
  const [detail, setDetail] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [photoCounts, setPhotoCounts] = useState({});
  const [photoFor, setPhotoFor] = useState(null); // {refId, dispatch_no}
  const [invBusyId, setInvBusyId] = useState(null);
  const [editShipmentId, setEditShipmentId] = useState(null);

  const uninvoicedCount = rows.filter(r => (r.invoice_status || "not_invoiced") === "not_invoiced" && r.status !== "cancelled").length;

  const toggleInvoice = async (r) => {
    if (!canWrite) return;
    const cur = r.invoice_status === "invoiced" ? "Kesildi" : "Kesilmedi";
    const nxt = r.invoice_status === "invoiced" ? "Kesilmedi" : "Kesildi";
    if (!window.confirm(`Fatura durumu değişecek:\n${r.dispatch_no}\n${cur} → ${nxt}\n\nDevam edilsin mi?`)) return;
    setInvBusyId(r.id);
    try {
      const res = await api.post(`/shipments/${r.id}/toggle-invoice`, {});
      // Local update — mevcut satırın invoice_status alanını güncelle
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, invoice_status: res.invoice_status, invoiced_at: res.invoiced_at, invoiced_by_name: res.invoiced_by_name } : x));
    } catch (e) { alert("❌ Fatura durumu güncellenemedi: " + (e.response?.data?.detail || e.message)); }
    finally { setInvBusyId(null); }
  };

  const loadPhotoCounts = async (ids) => {
    if (!ids?.length) return;
    try {
      const r = await api.get(`/photos/counts?context_type=SHIPMENT_EVIDENCE&ref_type=shipment&ref_ids=${ids.join(",")}`);
      setPhotoCounts(r || {});
    } catch { /* ignore */ }
  };

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusF) params.set("status", statusF);
      if (invoiceF) params.set("invoice_status", invoiceF);
      const r = await api.get(`/deliveries/grouped-history${params.toString() ? "?" + params.toString() : ""}`);
      setRows(r);
      loadPhotoCounts((r || []).map(x => x.id));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, invoiceF]);

  const openDetail = async (r) => {
    try { const d = await api.get(`/deliveries/${r.id}/detail`); setDetail(d); }
    catch (e) { alert(e.response?.data?.detail || "Detay alınamadı"); }
  };
  const openPdf = async (r) => {
    // Auth-guarded PDF fetch (window.open sends no Authorization header, causing 401)
    try {
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipments/${r.id}/irsaliye.pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { alert("PDF alınamadı: HTTP " + resp.status); return; }
      const blob = await resp.blob();
      openPdfPreview(blob, `Irsaliye-${r.dispatch_no || r.shipment_no || r.id}.pdf`);
    } catch (e) { alert("PDF açılamadı: " + e.message); }
  };
  const sendTelegram = async (r) => {
    if (!confirm(`İrsaliye ${r.dispatch_no} müşteriye Telegram ile gönderilsin mi?`)) return;
    try {
      const res = await api.post(`/deliveries/${r.id}/send-telegram`, {});
      alert(res.ok ? `✅ Telegram gönderildi (${res.customer_name} · ${res.dispatch_no})` :
        `⚠️ Gönderilemedi: ${res.response?.description || JSON.stringify(res.response).slice(0, 200)}`);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };
  const sendEmail = async (r) => {
    const ccMuh = confirm(`İrsaliye ${r.dispatch_no} müşteriye e-posta ile gönderilsin mi?\n\n[TAMAM] = Muhasebe CC'de gönder\n[İPTAL] = Muhasebe CC yok, sadece Vazgeçmek için sayfayı kapatın`);
    // Simpler flow: Two-step prompt kept — Tamam = with CC, İptal reload → we'll show a proper modal:
    try {
      const res = await api.post(`/deliveries/${r.id}/send-email`, { cc_muhasebe: ccMuh });
      alert(res.ok ? `✅ E-posta gönderildi (${res.customer_name} · ${res.dispatch_no})${res.cc?.length ? "\nCC: " + res.cc.join(", ") : ""}` :
        `⚠️ Gönderilemedi: ${res.response?.error || "Bilinmeyen hata"}`);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };
  const doCancel = async () => {
    if (!cancelReason.trim()) { alert("İptal nedeni zorunlu"); return; }
    try {
      await api.post(`/shipments/${cancelTarget.id}/cancel`, { reason: cancelReason });
      alert(`✅ Teslimat iptal edildi (${cancelTarget.dispatch_no}). Stok geri yüklendi.`);
      setCancelTarget(null); setCancelReason(""); load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const hpg = usePagination(rows, "finished-products-history", 100);

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400"/>
          <input data-testid="hist-search" value={q} onChange={e=>setQ(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && load()}
                 placeholder="İrsaliye / Müşteri / Sipariş / Üretim No..."
                 className="w-full pl-7 pr-3 py-1.5 text-sm border rounded"/>
        </div>
        <select value={statusF} onChange={e=>setStatusF(e.target.value)}
                className="text-sm border rounded px-2 py-1.5" data-testid="hist-status-filter">
          <option value="">Tüm Durumlar</option>
          <option value="shipped">Sevk Edildi</option>
          <option value="delivered">Teslim Edildi</option>
          <option value="cancelled">İptal</option>
        </select>
        {/* Fatura hızlı filtre — Fix Pkg 03 follow-up */}
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="hist-invoice-filter">
          <button
            type="button"
            onClick={() => setInvoiceF("")}
            data-testid="hist-invoice-filter-all"
            className={`px-2.5 py-1.5 border-r border-slate-200 ${invoiceF === "" ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            title="Tüm faturalar"
          >Tümü</button>
          <button
            type="button"
            onClick={() => setInvoiceF(invoiceF === "not_invoiced" ? "" : "not_invoiced")}
            data-testid="hist-invoice-filter-uninvoiced"
            className={`px-2.5 py-1.5 border-r border-slate-200 inline-flex items-center gap-1 ${invoiceF === "not_invoiced" ? "bg-amber-500 text-white" : "bg-white text-amber-700 hover:bg-amber-50"}`}
            title="Yalnızca faturası kesilmemiş irsaliyeler"
          >
            <span>○ Kesilmedi</span>
            {uninvoicedCount > 0 && (
              <span className={`tabular-nums font-mono rounded-full px-1.5 min-w-[18px] text-center ${invoiceF === "not_invoiced" ? "bg-white/20" : "bg-amber-100"}`}>{uninvoicedCount}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setInvoiceF(invoiceF === "invoiced" ? "" : "invoiced")}
            data-testid="hist-invoice-filter-invoiced"
            className={`px-2.5 py-1.5 ${invoiceF === "invoiced" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 hover:bg-emerald-50"}`}
            title="Yalnızca faturası kesilmiş irsaliyeler"
          >✓ Kesildi</button>
        </div>
        <Btn variant="secondary" onClick={load} data-testid="hist-refresh">Ara</Btn>
      </div>

      <Table testid="hist-table" rows={hpg.paged} columns={[
        { label: "İrsaliye No", render: r => (
          <button onClick={()=>openDetail(r)} className="font-mono text-xs text-blue-600 hover:underline">{r.dispatch_no}</button>
        ) },
        { label: "Teslim Tarihi", render: r => fmtDate(r.delivery_date) },
        { label: "Müşteri", render: r => r.customer_name || "—" },
        { label: "Sipariş", render: r => r.sales_order_count === 1 ? "1 Sipariş" : `${r.sales_order_count} Sipariş` },
        { label: "Kalem", render: r => `${r.item_count} Kalem` },
        { label: "Toplam Miktar", render: r => <span className="font-mono">{r.total_qty}</span> },
        { label: "İşlemi Yapan", render: r => r.created_by_name || "—" },
        { label: "Foto", render: r => (
          <PhotoBadgeButton
            count={photoCounts[r.id] || 0}
            onClick={() => setPhotoFor({ refId: r.id, dispatch_no: r.dispatch_no })}
            testid={`hist-photo-${r.id}`}
            title="Sevkiyat Fotoğrafları"
          />
        )},
        { label: "Fatura", render: r => {
          const invoiced = r.invoice_status === "invoiced";
          const busyThis = invBusyId === r.id;
          const canceled = r.status === "cancelled";
          const tooltip = invoiced && r.invoiced_by_name
            ? `${r.invoiced_by_name} · ${fmtDate(r.invoiced_at)}`
            : invoiced ? "Faturası kesildi" : "Fatura henüz kesilmedi";
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (!canceled) toggleInvoice(r); }}
              disabled={!canWrite || canceled || busyThis}
              title={tooltip}
              data-testid={`hist-invoice-${r.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 whitespace-nowrap
                ${invoiced
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20 hover:bg-emerald-100"
                  : "bg-amber-50 text-amber-700 ring-amber-600/20 hover:bg-amber-100"}
                ${(!canWrite || canceled) ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {busyThis ? "…" : invoiced ? "✓ Kesildi" : "○ Kesilmedi"}
            </button>
          );
        }},
        { label: "Durum", render: r => {
          const lvl = r.status === "cancelled" ? "danger" : r.status === "delivered" ? "ok" : "attention";
          const lbl = r.status === "cancelled" ? "İPTAL" : r.status === "delivered" ? "Teslim Edildi" : r.status === "shipped" ? "Sevk Edildi" : r.status;
          return <Badge level={lvl}>{lbl}</Badge>;
        } },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e=>e.stopPropagation()}>
            {/* iter330 — İkon-only aksiyonlar + native title tooltip (1 sn hover). */}
            <Btn size="sm" variant="secondary" onClick={()=>openDetail(r)} data-testid={`hist-detail-${r.id}`} title="Detay"><Eye size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={()=>openPdf(r)} data-testid={`hist-pdf-${r.id}`} title="İrsaliye PDF">
              <FileText size={12}/>
            </Btn>
            {r.status !== "cancelled" && (
              <>
                <Btn size="sm" variant="secondary" onClick={()=>sendTelegram(r)}
                     className="text-sky-600" data-testid={`hist-tg-${r.id}`}
                     title="İrsaliyeyi Telegram ile Müşteriye Gönder">
                  <Send size={12}/>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={()=>sendEmail(r)}
                     className="text-fuchsia-600" data-testid={`hist-email-${r.id}`}
                     title="İrsaliyeyi E-posta ile Müşteriye Gönder">
                  <Mail size={12}/>
                </Btn>
              </>
            )}
            {isAdmin && r.status !== "cancelled" && (
              <>
                <Btn size="sm" variant="secondary" onClick={()=>setEditShipmentId(r.id)}
                     className="text-amber-700" data-testid={`hist-edit-${r.id}`}
                     title="Teslimatı Düzenle (adet & teslim alan)">
                  <Pencil size={12}/>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={()=>setCancelTarget(r)}
                     className="text-red-600" data-testid={`hist-cancel-${r.id}`}
                     title="Teslimatı İptal Et">
                  <X size={12}/>
                </Btn>
              </>
            )}
          </div>
        )},
      ]}/>
      <ListPagination pagination={hpg} testid="hist-pagination"/>
      {busy && <div className="text-xs text-slate-500 mt-2">Yükleniyor...</div>}
      {!busy && rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Kayıt yok</div>}

      {/* Detay modalı */}
      <Modal open={!!detail} onClose={()=>setDetail(null)} title={`Teslimat Detayı — ${detail?.dispatch_no || ""}`}>
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded text-xs">
              <div><b>Müşteri:</b> {detail.customer_name}</div>
              <div><b>Tarih:</b> {fmtDate(detail.delivery_date)}</div>
              <div><b>İşlemi Yapan:</b> {detail.created_by_name || "—"}</div>
              <div><b>Taşıyıcı:</b> {detail.carrier || "—"}</div>
              <div><b>Araç:</b> {detail.vehicle || "—"}</div>
              <div><b>Takip:</b> {detail.tracking_no || "—"}</div>
              {detail.status === "cancelled" && (
                <div className="col-span-2 bg-red-50 p-2 rounded border border-red-200">
                  <b>İPTAL:</b> {detail.cancel_reason} · {fmtDate(detail.cancelled_at)}
                </div>
              )}
              {detail.notes && <div className="col-span-2"><b>Açıklama:</b> {detail.notes}</div>}
            </div>
            <table className="w-full text-xs border">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-1.5 text-left">Sipariş</th>
                  <th className="p-1.5 text-left">Üretim</th>
                  <th className="p-1.5 text-left">Proje</th>
                  <th className="p-1.5 text-left">Ürün</th>
                  <th className="p-1.5 text-right">Sip. Mik.</th>
                  <th className="p-1.5 text-right">T. Dolum</th>
                  <th className="p-1.5 text-right">Önceki Tes.</th>
                  <th className="p-1.5 text-right">Bu Tes.</th>
                  <th className="p-1.5 text-right">Kalan</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((it, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1.5 font-mono">{it.sales_order_no}</td>
                    <td className="p-1.5 font-mono">{it.prod_no}</td>
                    <td className="p-1.5">{it.project_no}</td>
                    <td className="p-1.5">{it.product_name}</td>
                    <td className="p-1.5 text-right font-mono">{it.order_qty}</td>
                    <td className="p-1.5 text-right font-mono">{it.total_filled}</td>
                    <td className="p-1.5 text-right font-mono">{it.previous_delivered}</td>
                    <td className="p-1.5 text-right font-mono font-bold">{it.this_delivery} {it.unit}</td>
                    <td className="p-1.5 text-right font-mono">{it.remaining_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={()=>setDetail(null)}>Kapat</Btn>
              <Btn variant="secondary" onClick={()=>openPdf(detail)} title="İrsaliye PDF"><FileText size={12}/></Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* İptal modalı */}
      <Modal open={!!cancelTarget} onClose={()=>{ setCancelTarget(null); setCancelReason(""); }} title={`Teslimatı İptal Et — ${cancelTarget?.dispatch_no || ""}`}>        {cancelTarget && (
          <div>
            <div className="text-sm mb-3 bg-red-50 p-3 rounded border border-red-200">
              ⚠️ İptal sonrası bu irsaliye <b>{cancelTarget.item_count} kalem</b> ({cancelTarget.total_qty} adet) stoktan iade edilir.
              Sales Order status'ları yeniden hesaplanır. İrsaliye numarası tekrar kullanılmaz.
            </div>
            <Textarea label="İptal Nedeni (zorunlu)" rows={3} value={cancelReason}
                      onChange={e=>setCancelReason(e.target.value)} data-testid="cancel-reason"/>
            <div className="flex gap-2 mt-3">
              <Btn variant="secondary" onClick={()=>{ setCancelTarget(null); setCancelReason(""); }}>Vazgeç</Btn>
              <div className="flex-1"/>
              <Btn onClick={doCancel} className="bg-red-600 hover:bg-red-700 text-white"
                   data-testid="cancel-confirm-btn">Teslimatı İptal Et</Btn>
            </div>
          </div>
        )}
      </Modal>
      <PhotoModal
        open={!!photoFor}
        onClose={() => { setPhotoFor(null); loadPhotoCounts(rows.map(r => r.id)); }}
        contextType="SHIPMENT_EVIDENCE"
        refType="shipment"
        refId={photoFor?.refId}
        title={`Sevkiyat Fotoğrafları${photoFor?.dispatch_no ? ` — ${photoFor.dispatch_no}` : ""}`}
        showTelegram
        watermark
      />
      {/* Düzenleme modalı — admin (iter294b) */}
      <EditDeliveryModal
        shipmentId={editShipmentId}
        onClose={()=>setEditShipmentId(null)}
        onDone={()=>{ setEditShipmentId(null); load(); }}
      />
    </>
  );
}

/* ---------- Ana sayfa ---------- */
export function FinishedProductsPage() {
  const [tab, setTab] = useState("stock");
  return (
    <div>
      <PageHeader title="Bitmiş Ürünler & Teslimat"
                  description="Aynı Üretim No = TEK STOK. Tekli veya toplu teslimat 1 İrsaliye oluşturur. Dolum geçmişi ayrı korunur."/>
      <div className="flex gap-1 mb-3 border-b">
        <button onClick={()=>setTab("stock")}
                className={`px-4 py-2 text-sm ${tab==="stock" ? "border-b-2 border-emerald-600 text-emerald-700 font-medium" : "text-slate-500 hover:text-slate-800"}`}
                data-testid="tab-stock">
          <Package size={14} className="inline mr-1"/> Stok
        </button>
        <button onClick={()=>setTab("history")}
                className={`px-4 py-2 text-sm ${tab==="history" ? "border-b-2 border-emerald-600 text-emerald-700 font-medium" : "text-slate-500 hover:text-slate-800"}`}
                data-testid="tab-history">
          <History size={14} className="inline mr-1"/> Teslimat Geçmişi
        </button>
      </div>
      {tab === "stock" ? <StockTab/> : <HistoryTab/>}
    </div>
  );
}

export default FinishedProductsPage;
